import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { tenantService, Tenant, TenantCreate } from '../services/tenantService';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import { formatDate } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';

const TenantsPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const userTimezone = useUserTimezone();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // UI State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<TenantCreate & { is_active?: boolean }>();

  const crud = useCrudHandlers<Tenant>({
    table: { data: tenants, setData: setTenants },
    createFn: (data) => { const { is_active, ...tenantData } = data; return tenantService.createTenant(tenantData); },
    updateFn: (id, data) => { const { is_active, ...tenantData } = data; return tenantService.updateTenant(id as number, tenantData); },
    deleteFn: (id) => tenantService.deleteTenant(id as number),
    entityName: 'Tenant',
    onSuccess: () => { setAddDialogOpen(false); setEditingTenant(null); reset(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setSelectedTenant(null); },
  });

  // Filter tenants based on search query
  const filteredTenants = tenants.filter(tenant => {
    const query = searchQuery.toLowerCase();
    return (
      tenant.name.toLowerCase().includes(query) ||
      tenant.slug.toLowerCase().includes(query) ||
      (tenant.contact_email && tenant.contact_email.toLowerCase().includes(query)) ||
      (tenant.description && tenant.description.toLowerCase().includes(query))
    );
  });

  // Load data
  useEffect(() => {
    const loadTenantsData = async () => {
      try {
        setLoading(true);
        setError(null);

        const tenantsData = await tenantService.getTenants();
        setTenants(tenantsData);
      } catch (err) {
        console.error('Error loading tenants data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tenants data');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.tenant_id === 0) {
      loadTenantsData();
    }
  }, [currentUser]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, tenant: Tenant) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenant(tenant);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTenant(null);
  };

  const handleEdit = () => {
    if (selectedTenant) {
      setEditingTenant(selectedTenant);
      reset({
        name: selectedTenant.name,
        slug: selectedTenant.slug,
        description: selectedTenant.description || '',
        contact_email: selectedTenant.contact_email || '',
        contact_phone: selectedTenant.contact_phone || '',
        address: selectedTenant.address || '',
        is_active: selectedTenant.is_active,
      });
      setAddDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedTenant) {
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleAddTenant = () => {
    setEditingTenant(null);
    reset({
      name: '',
      slug: '',
      description: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      is_active: true,
    });
    setAddDialogOpen(true);
  };

  const onSubmit = (data: TenantCreate & { is_active?: boolean }) => {
    crud.handleCreateOrUpdate(data, editingTenant);
  };

  const handleConfirmDelete = () => {
    crud.handleDelete(selectedTenant);
  };

  if (currentUser?.tenant_id !== 0) {
    return (
      <Alert severity="error">
        You don't have permission to access this page.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading tenants: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Tenant Management ({filteredTenants.length}{tenants.length !== filteredTenants.length ? ` of ${tenants.length}` : ''})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTenant}
        >
          Add Tenant
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Search tenants by name, slug, email, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Contact Info</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTenants.map((tenant) => (
              <TableRow key={tenant.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <BusinessIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {tenant.name}
                      </Typography>
                      {tenant.address && (
                        <Typography variant="caption" color="text.secondary">
                          {tenant.address}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {tenant.slug}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    {tenant.contact_email && (
                      <Typography variant="caption" display="block">
                        {tenant.contact_email}
                      </Typography>
                    )}
                    {tenant.contact_phone && (
                      <Typography variant="caption" display="block">
                        {tenant.contact_phone}
                      </Typography>
                    )}
                    {!tenant.contact_email && !tenant.contact_phone && (
                      <Typography variant="caption" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {tenant.description || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={tenant.is_active ? 'Active' : 'Inactive'}
                    color={tenant.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(tenant.created_at, userTimezone)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, tenant)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Tenant Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
          </DialogTitle>
          <DialogContent>
            <TextField
              {...register('name', { required: 'Tenant name is required' })}
              fullWidth
              label="Tenant Name"
              margin="normal"
              error={!!errors.name}
              helperText={errors.name?.message as string}
            />

            <TextField
              {...register('slug', {
                required: 'Slug is required',
                pattern: {
                  value: /^[a-z0-9-]+$/,
                  message: 'Slug must contain only lowercase letters, numbers, and hyphens'
                }
              })}
              fullWidth
              label="Slug"
              margin="normal"
              error={!!errors.slug}
              helperText={errors.slug?.message as string || 'Used in URLs (e.g., clinic-name)'}
            />

            <TextField
              {...register('description')}
              fullWidth
              label="Description"
              multiline
              rows={3}
              margin="normal"
            />

            <Box display="flex" gap={2}>
              <TextField
                {...register('contact_email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                fullWidth
                label="Contact Email"
                type="email"
                margin="normal"
                error={!!errors.contact_email}
                helperText={errors.contact_email?.message as string}
              />

              <TextField
                {...register('contact_phone')}
                fullWidth
                label="Contact Phone"
                margin="normal"
              />
            </Box>

            <TextField
              {...register('address')}
              fullWidth
              label="Address"
              multiline
              rows={2}
              margin="normal"
            />

            {editingTenant && (
              <FormControlLabel
                control={
                  <Switch
                    {...register('is_active')}
                    defaultChecked={editingTenant.is_active}
                  />
                }
                label="Active Tenant"
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingTenant ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        entityType="Tenant"
        entityName={selectedTenant?.name}
        additionalWarning="This action cannot be undone and will affect all users and data associated with this tenant."
      />
    </Box>
  );
};

export default TenantsPage;